import startCase from 'lodash/startCase';

import {
  Button,
  Caption,
  Icon,
  Input,
  LoadingIcon,
  Stack,
  Text,
} from '@eluve/components';
import { pluralize } from '@eluve/utils';

import { PdfPreview } from '../PdfPreview';

export type ReviewField = {
  name: string;
  type: 'text' | 'checkbox' | 'radio' | 'dropdown';
  /** User-facing label from the PDF; absent for fillable PDFs without one. */
  label?: string;
};

// Prefer the PDF's own label; otherwise humanize the normalized key
// (patient_name -> "Patient Name"), leaving already-human names untouched.
const fieldLabel = ({ name, label }: ReviewField): string =>
  label?.trim() || (name.includes(' ') ? name : startCase(name));

export type ReviewFillableFieldsViewProps = {
  fileName: string;
  fields: ReviewField[];
  alreadyFillable: boolean;
  previewUrl: string | null;
  name: string;
  onNameChange: (name: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
};

export const ReviewFillableFieldsView: React.FC<
  ReviewFillableFieldsViewProps
> = ({
  fileName,
  fields,
  alreadyFillable,
  previewUrl,
  name,
  onNameChange,
  onSave,
  onCancel,
  isSaving,
}) => {
  const saveDisabled = !name.trim() || isSaving;

  return (
    <Stack gap={4}>
      <Stack
        className="rounded-lg bg-bg-indigo-subtle-primary px-4 py-3"
        direction="horizontal"
        gap={2}
        align="center"
      >
        <Icon name="CheckCircle2" size="sm" className="text-text-indigo" />
        <Text size="sm">
          {alreadyFillable
            ? `${fileName} is already a fillable form · ${pluralize(fields.length, 'field')}`
            : `Eluve added ${pluralize(fields.length, 'form field')} to ${fileName}. Review, then save as a template.`}
        </Text>
      </Stack>

      <Stack direction="horizontal" gap={4} align="start">
        <div className="min-w-0 flex-1">
          {previewUrl ? (
            <PdfPreview
              file={previewUrl}
              className="max-h-[55vh]"
              highlightFields
            />
          ) : (
            <Stack align="center" justify="center" className="p-6">
              <LoadingIcon size="lg" />
            </Stack>
          )}
        </div>

        <Stack gap={2} className="w-56 shrink-0">
          <Caption size="2xs" weight="bold" color="tertiary">
            DETECTED FIELDS · {fields.length}
          </Caption>
          <Stack gap={1} className="max-h-[55vh] overflow-y-auto pr-1">
            {fields.map((field, i) => (
              <Stack
                key={`${field.name}-${i}`}
                direction="horizontal"
                gap={2}
                align="start"
                justify="between"
              >
                <Text size="sm" className="min-w-0 flex-1">
                  {fieldLabel(field)}
                </Text>
                <span className="mt-0.5 shrink-0 rounded-full bg-bg-tertiary px-2 py-0.5">
                  <Caption size="2xs" color="tertiary" inline>
                    {field.type}
                  </Caption>
                </span>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>

      <Stack gap={1}>
        <Stack direction="horizontal" gap={2} align="center">
          <Text size="xs" color="secondary">
            Template name
          </Text>
          {!name.trim() && (
            <Text inline size="xs" className="text-text-negative">
              Required
            </Text>
          )}
        </Stack>
        <Input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Name your template"
        />
      </Stack>

      <Stack direction="horizontal" justify="between" align="center" gap={2}>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={saveDisabled}>
          {isSaving ? 'Saving…' : 'Save as template'}
        </Button>
      </Stack>
    </Stack>
  );
};
